import { useRef, useState, useCallback, useEffect } from 'react';

interface UseAutoSubmitParams {
  shouldAutoSubmit: boolean;
  onAutoSubmit: () => void;
}

interface UseAutoSubmitReturn {
  formRef: React.RefObject<HTMLFormElement>;
  handleFieldFocus: () => void;
  handleFieldBlur: () => void;
  isAnyFieldFocused: boolean;
}

export const DEFAULT_DEBOUNCE_MS = 300;

export const useAutoSubmit = ({
  shouldAutoSubmit,
  onAutoSubmit,
}: UseAutoSubmitParams): UseAutoSubmitReturn => {
  const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const formRef = useRef<HTMLFormElement>(null);
  const [isAnyFieldFocused, setIsAnyFieldFocused] = useState(false);

  const handleFieldFocus = useCallback(() => {
    setIsAnyFieldFocused(true);
    // clear any pending auto-submit when user starts editing again
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = undefined;
    }

    // clear any pending blur timeout when user starts editing again
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = undefined;
    }
  }, []);

  const handleFieldBlur = useCallback(() => {
    // clear any existing blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = undefined;
    }

    // very short setTimeout to check if focus moved to another field within the form
    // this prevents false negatives when tabbing between form fields
    blurTimeoutRef.current = setTimeout(() => {
      const activeElement = document.activeElement;

      // check if focus is on a market-select or market-popover element
      // this is a workaround for the market-select and market-popover elements
      // there are some weird issues with using the form's onBlur and onFocus events
      // that cause the market-select to not trigger its onMarketSelectValueDidChange event
      // most likely due to the market-select and market-popover elements aren't using native inputs
      const isMarketSelectActive = activeElement?.closest?.(
        'market-select, market-popover, market-list, market-row',
      );

      if (
        formRef.current &&
        !formRef.current.contains(activeElement) &&
        !isMarketSelectActive
      ) {
        setIsAnyFieldFocused(false);
      }

      blurTimeoutRef.current = undefined;
    }, 10);
  }, []);

  useEffect(() => {
    // clear any existing timeout when dependencies change
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = undefined;
    }

    // only trigger auto submit if the prop is true and no field is currently focused
    if (shouldAutoSubmit && !isAnyFieldFocused) {
      autoSubmitTimeoutRef.current = setTimeout(() => {
        onAutoSubmit();
        autoSubmitTimeoutRef.current = undefined;
      }, DEFAULT_DEBOUNCE_MS);
    }

    // cleanup
    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = undefined;
      }
    };
  }, [shouldAutoSubmit, isAnyFieldFocused, onAutoSubmit]);

  // cleanup
  useEffect(() => {
    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return {
    formRef,
    handleFieldFocus,
    handleFieldBlur,
    isAnyFieldFocused,
  };
};
